import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Input, InputGroup
} from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import DataTable from "react-data-table-component";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { FileText, Download } from 'react-feather';
import DatePicker from "react-datepicker";
import { useConfig } from "../../Context/ConfigContext";
import { useAuth } from "../../Context/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return "N/A";
    const dateObj = new Date(dateString);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

interface PatientInsightData {
    patient_id: number;
    patient_name: string;
    patient_mobile: string;
    account_created: string;
    last_booking_date: string;
    total_bookings: number;
    lifetime_value: number;
}

interface PatientInsightWithFooter extends Partial<PatientInsightData> {
    isFooter?: boolean;
}

export default function PatientInsightsReport() {
    const [data, setData] = useState<PatientInsightData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("lifetime_value");
    const [sortDir, setSortDir] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [summary, setSummary] = useState({ total_bookings: 0, lifetime_value: 0 });
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const { config } = useConfig();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const response: any = await apiPost(`report/patient-insights-report?page=${currentPage}`, {
                per_page: perPage,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            setData(response.data.data || []);
            setTotalRows(response.data.total || 0);
            setSummary(response.summary || { total_bookings: 0, lifetime_value: 0 });
        } catch (error) {
            toast.error("Failed to load patient insights report");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, debouncedSearch, startDate, endDate, sortBy, sortDir]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, startDate, endDate]);

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'lifetime_value');
        setSortDir(sortDirection);
    };

    const fetchAllData = async () => {
        try {
            const response: any = await apiPost(`report/patient-insights-report?page=1`, {
                per_page: -1,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            return response.data.data || [];
        } catch (error) {
            console.error("Error fetching all data:", error);
            return [];
        }
    };

    const exportToExcel = async (exportDataItems: any[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Patient Insights');

        const headerStyle: Partial<ExcelJS.Style> = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00695C' } },
            alignment: { vertical: 'middle', horizontal: 'center' }
        };

        worksheet.columns = [
            { header: 'Sl.No', key: 'sl', width: 8 },
            { header: 'Patient Name', key: 'name', width: 30 },
            { header: 'Account Created', key: 'created', width: 15 },
            { header: 'Last Booking', key: 'last', width: 15 },
            { header: 'Total Bookings', key: 'bookings', width: 15 },
            { header: `Lifetime Value (${config.currency})`, key: 'ltv', width: 25 },
        ];

        worksheet.getRow(1).eachCell((cell) => {
            cell.style = headerStyle;
        });

        exportDataItems.forEach((item, index) => {
            const row = worksheet.addRow({
                sl: index + 1,
                name: `${item.patient_name || "N/A"}\n${item.patient_mobile || ""}`,
                created: formatDateForDisplay(item.account_created),
                last: formatDateForDisplay(item.last_booking_date),
                bookings: Number(item.total_bookings || 0),
                ltv: Number(item.lifetime_value || 0)
            });

            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
        });

        const summaryRow = worksheet.addRow({
            sl: '',
            name: 'TOTAL',
            bookings: summary.total_bookings,
            ltv: summary.lifetime_value
        });

        summaryRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Patient_Insights_Report_${formatDate(new Date())}.xlsx`);
    };

    const exportToPDF = (exportDataItems: any[]) => {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.text("Patient Insights & LTV Report", 14, 15);

        const currencyMap: Record<string, string> = {
            '₹': 'Rs.', '€': 'EUR', '£': 'GBP', '¥': 'YEN',
            '₽': 'RUB', '₩': 'KRW', '฿': 'THB', '₪': 'ILS', '₫': 'VND', '₱': 'PHP'
        };
        const pdfCurrency = currencyMap[config.currency] || config.currency;
        const tableColumn = ["Sl.No", "Patient Info", "Account Created", "Last Booking", "Total Bookings", `LTV (${pdfCurrency})`];
        const tableRows = exportDataItems.map((item, index) => {
            return [
                index + 1,
                `${item.patient_name || "N/A"}\n${item.patient_mobile || ""}`,
                formatDateForDisplay(item.account_created),
                formatDateForDisplay(item.last_booking_date),
                item.total_bookings,
                Number(item.lifetime_value || 0).toFixed(2)
            ];
        });

        tableRows.push([
            "",
            "TOTAL",
            "",
            "",
            String(summary.total_bookings || 0),
            Number(summary.lifetime_value || 0).toFixed(2)
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'grid',
            headStyles: { fillColor: [0, 105, 92] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 15 },
                4: { halign: 'center' },
                5: { fontStyle: 'bold', halign: 'right', textColor: [0, 105, 92] }
            },
            didParseCell: (cellData: any) => {
                if (cellData.row.index === tableRows.length - 1) {
                    cellData.cell.styles.fontStyle = 'bold';
                    cellData.cell.styles.fillColor = [245, 245, 245];
                }
            }
        });
        doc.save(`Patient_Insights_Report_${formatDate(new Date())}.pdf`);
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: PatientInsightWithFooter, index: number) =>
                row.isFooter ? "" : (currentPage - 1) * perPage + (index ?? 0) + 1,
            width: "80px",
        },
        {
            name: <div><span className="fw-bold">Patient Info</span></div>,
            cell: (row: PatientInsightWithFooter) => {
                if (row.isFooter) return "";
                return (
                    <div className="py-2">
                        <div className="fw-bold text-dark">{row.patient_name || "N/A"}</div>
                        <div className="small text-muted">{row.patient_mobile || ""}</div>
                    </div>
                );
            },
            sortable: (row: PatientInsightWithFooter) => !row.isFooter,
            sortField: "patient_name",
            width: "250px",
        },
        {
            name: <div><span className="fw-bold">Account Created</span></div>,
            cell: (row: PatientInsightWithFooter) => {
                if (row.isFooter) return <span className="fw-bold" style={{ fontSize: '14px', color: '#00695c' }}>TOTAL:</span>;
                return (
                    <div className="py-2 text-muted small">
                        {formatDateForDisplay(row.account_created || "")}
                    </div>
                );
            },
            sortable: (row: PatientInsightWithFooter) => !row.isFooter,
            sortField: "account_created",
        },
        {
            name: <div><span className="fw-bold">Last Booking</span></div>,
            cell: (row: PatientInsightWithFooter) => {
                if (row.isFooter) return "";
                return (
                    <div className="py-2 text-muted small">
                        {formatDateForDisplay(row.last_booking_date || "")}
                    </div>
                );
            },
            sortable: (row: PatientInsightWithFooter) => !row.isFooter,
            sortField: "last_booking_date",
        },
        {
            name: <div><span className="fw-bold">Total Bookings</span></div>,
            sortable: (row: PatientInsightWithFooter) => !row.isFooter,
            sortField: "total_bookings",
            cell: (row: PatientInsightWithFooter) => (
                <div className="w-100 fw-bold px-2" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : '#1e3a8a' }}>
                    {row.total_bookings}
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Lifetime Value ({config.currency})</span></div>,
            sortable: (row: PatientInsightWithFooter) => !row.isFooter,
            sortField: "lifetime_value",
            cell: (row: PatientInsightWithFooter) => (
                <div className="w-100 fw-bold px-2 text-success" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.lifetime_value || 0).toLocaleString()}
                </div>
            )
        },
    ];

    const columnsData: PatientInsightWithFooter[] = data.length > 0 ? [
        ...data,
        {
            isFooter: true,
            total_bookings: summary.total_bookings,
            lifetime_value: summary.lifetime_value
        }
    ] : [];

    const conditionalRowStyles = [
        {
            when: (row: PatientInsightWithFooter) => !!row.isFooter,
            style: {
                fontWeight: "800",
                backgroundColor: "#f8f9fa",
                color: "#1e3a8a",
                borderTop: "2px solid #dee2e6",
                fontSize: "16px",
            },
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                color: '#308e87',
                minHeight: '50px',
                borderBottom: '2px solid #308e87',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px', borderBottom: "1px solid #dee2e6" },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Patient Insights Report" parent="Reports" title="LTV Analytics" />
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Patient Insights</H3>
                            <div className="d-flex gap-2">
                                {(hasPermission('patient-insights.download_pdf') || hasPermission('patient-insights-report.download_pdf')) && (
                                    <Button
                                        color="success"
                                        onClick={async () => {
                                            setPdfLoading(true);
                                            const allData = await fetchAllData();
                                            exportToPDF(allData);
                                            setPdfLoading(false);
                                        }}
                                        disabled={pdfLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0"
                                        style={{ borderRadius: '6px' }}
                                        title="Export PDF"
                                    >
                                        {pdfLoading ? <Spinner size="sm" /> : <FileText size={16} />}
                                        <span className="fw-bold small">{pdfLoading ? 'PREPARING...' : 'PDF'}</span>
                                    </Button>
                                )}
                                {(hasPermission('patient-insights.download_excel') || hasPermission('patient-insights-report.download_excel')) && (
                                    <Button
                                        color="warning"
                                        onClick={async () => {
                                            setExcelLoading(true);
                                            const allData = await fetchAllData();
                                            await exportToExcel(allData);
                                            setExcelLoading(false);
                                        }}
                                        disabled={excelLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0 text-white"
                                        style={{ backgroundColor: '#ffaf00', borderRadius: '6px' }}
                                        title="Export Excel"
                                    >
                                        {excelLoading ? <Spinner size="sm" /> : <Download size={16} />}
                                        <span className="fw-bold small">{excelLoading ? 'PREPARING...' : 'EXCEL'}</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className=" mb-4">
                            <Row className="align-items-center justify-content-end g-3">
                                <Col xl={5} lg={7} md={12} className="d-flex align-items-center gap-2">
                                    <span className="small text-muted fw-bold text-uppercase ms-2" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>FROM:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={startDate ? new Date(startDate) : null}
                                            onChange={(date: Date) => setStartDate(date ? formatDate(date) : "")}
                                            placeholderText="Start date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                    <span className="small text-muted fw-bold text-uppercase" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>TO:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={endDate ? new Date(endDate) : null}
                                            onChange={(date: Date) => setEndDate(date ? formatDate(date) : "")}
                                            placeholderText="End date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                </Col>

                                <Col xl={3} lg={5} md={12}>
                                    <InputGroup className="shadow-sm">
                                        <Input
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-white"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </div>

                        <div className="border rounded">
                            <DataTable
                                columns={columns}
                                data={columnsData}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangeRowsPerPage={(newSize: number, page: number) => setPerPage(newSize)}
                                onChangePage={(page: number) => setCurrentPage(page)}
                                sortServer
                                onSort={handleSort}
                                highlightOnHover
                                customStyles={customStyles}
                                conditionalRowStyles={conditionalRowStyles}
                                noDataComponent={
                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                            <CardBody>
                                                <h6 className="fw-semibold mb-1">No Records Found</h6>
                                                <small className="text-muted">No patient insights data matching your criteria.</small>
                                            </CardBody>
                                        </Card>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
